import React, { useCallback, useEffect, useState } from "react";
import { api, describeError } from "../lib/api";
import { useAuth } from "../lib/auth";

const INK = "#141412";
const PHONE_RETURN = "/response/Thankyou.html";

const Mark = ({ size = 30 }) => (
  <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: size, height: size, borderRadius: 8, background: INK }}>
    <img src="/revQ_emblem_black.webp" alt="Aria One" style={{ width: size * 0.62, height: size * 0.62, objectFit: "contain", filter: "invert(1) brightness(2)" }} />
  </span>
);

const Spinner = ({ dark }) => (
  <span className="revq-spinner" style={{ borderTopColor: dark ? "#000" : "#fff", borderColor: dark ? "rgba(0,0,0,.18)" : "rgba(255,255,255,.4)" }} />
);

const PASS = ["verified", "not_required"];

/* Global post-login identity-verification gate (Didit, via the /edi API).
   Additive: sits after the BAA gate and before any protected content.
   Drives the four /api/edi/verification/* endpoints and polls /status as the
   single source of truth. Never auto-starts a session (each is a counted,
   billable attempt) — a session is only started when the user clicks. */
export default function VerificationGate() {
  const { token, signOut } = useAuth();
  const [st, setSt] = useState(null);          // /status payload
  const [baaOk, setBaaOk] = useState(true);    // let the BAA gate go first
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [checking, setChecking] = useState(true);   // first status fetch in flight
  const [statusErr, setStatusErr] = useState(false); // last status fetch failed

  const fetchStatus = useCallback(async () => {
    const token = sessionStorage.getItem("revq.token");
    if (!token) { setSt(null); return; }
    try {
      const b = await api.get("/api/edi/baa/status");
      if (b.data?.needs_acceptance) { setBaaOk(false); setSt(null); setChecking(false); return; }
      setBaaOk(true);
    } catch { setBaaOk(true); }
    try {
      const r = await api.get("/api/edi/verification/status");
      setSt(r.data); setStatusErr(false);
    } catch { setSt(null); setStatusErr(true); } finally { setChecking(false); }
  }, []);

  useEffect(() => {
    if (!token) { setSt(null); setChecking(true); setStatusErr(false); return undefined; }
    setChecking(true);
    fetchStatus();
    const onFocus = () => fetchStatus();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [token, fetchStatus]);

  // Fail CLOSED: once signed in (and BAA satisfied), the app stays blocked until
  // we can POSITIVELY confirm a passing status (verified / not_required). A null
  // status (fetch failed or still loading) blocks, it does not let the user in.
  // Golden rule (per exempt-handling spec): pass when the server says verification
  // is not required — this covers BOTH genuinely verified AND admin-exempt logins
  // (an exempt login reports state:"not_required"). Fall back to the state check
  // for older payloads. A null status still blocks (fail-closed).
  const passed = !!st && (st.verification_required === false || PASS.includes(st.state));
  const blocking = !!token && baaOk && !passed;

  // Keep re-checking while blocked — catches the flip to verified AND recovers
  // from a transient status-fetch error.
  useEffect(() => {
    if (!blocking) return undefined;
    const iv = setInterval(fetchStatus, 5000);
    return () => clearInterval(iv);
  }, [blocking, fetchStatus]);

  // Countdown ticker off the server-provided cooldown.
  useEffect(() => { setCooldown(st?.cooldown_seconds || 0); }, [st?.cooldown_seconds]);
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const startSession = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/api/edi/verification/sessions", {
        origin_url: window.location.origin,
        return_url: `${window.location.origin}${PHONE_RETURN}`,
      });
      if (data?.verification_url) window.open(data.verification_url, "_blank", "noopener,noreferrer");
      fetchStatus();
    } catch (e) { setErr(describeError(e)); } finally { setBusy(false); }
  };

  const openActive = () => {
    if (st?.active_url) window.open(st.active_url, "_blank", "noopener,noreferrer");
  };

  // Pass only on a positively confirmed passing status; otherwise block.
  if (!token || !baaOk || passed) return null;

  // No confirmed status yet (loading or fetch failed) → block with a checking /
  // retry overlay instead of letting the user through.
  if (!st) {
    return (
      <div data-testid="verify-gate" style={{ position: "fixed", inset: 0, zIndex: 9000, background: "#F1F1EF", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 20 }}>
        <div style={{ width: "min(460px, 96vw)", background: "#fff", border: "1px solid #E6E6E4", borderRadius: 18, boxShadow: "0 30px 80px rgba(20,20,18,.18)", padding: "26px 24px 20px", textAlign: "center" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}><Mark size={30} /></div>
          {statusErr && !checking ? (
            <>
              <h2 data-testid="verify-title" style={{ fontSize: 18, fontWeight: 800, color: INK, margin: "0 0 8px" }}>We couldn't confirm your verification</h2>
              <p data-testid="verify-body" style={{ fontSize: 13.5, lineHeight: 1.55, color: "#4A4A46", margin: "0 0 16px" }}>We can't check your identity-verification status right now, so access stays locked for your protection. Please check your connection and try again.</p>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12 }}>
                <button onClick={signOut} data-testid="verify-signout" style={{ fontSize: 12.5, fontWeight: 700, color: "#6C6C69", background: "transparent", border: 0, cursor: "pointer" }}>Sign out</button>
                <button onClick={() => { setChecking(true); fetchStatus(); }} data-testid="verify-retry" style={btnPrimary(true)}>Try again</button>
              </div>
            </>
          ) : (
            <>
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}><Spinner dark /></div>
              <h2 data-testid="verify-title" style={{ fontSize: 18, fontWeight: 800, color: INK, margin: "0 0 6px" }}>Checking your verification…</h2>
              <p data-testid="verify-body" style={{ fontSize: 13, lineHeight: 1.55, color: "#6C6C69", margin: 0 }}>One moment while we confirm your identity-verification status.</p>
            </>
          )}
        </div>
      </div>
    );
  }

  const state = st.state;
  const canStart = !!st.can_start && cooldown <= 0 && !st.active_url;
  const hasActive = !!st.active_url;
  const attemptsLeft = Number.isFinite(st.attempts_remaining) ? st.attempts_remaining : null;

  // ── Per-state copy ──────────────────────────────────────────────────────
  const COPY = {
    pending: { kicker: "Action required · identity", title: "Verify your identity to continue", body: "Before you can access patient data in AriaOne, we need to confirm who you are. It takes a couple of minutes — you'll use your phone to scan a government ID and take a quick selfie." },
    reverify_required: { kicker: "Action required · identity", title: "Please re-verify your identity", body: "For your account's security, we need you to confirm your identity again before continuing. It only takes a couple of minutes on your phone." },
    in_progress: { kicker: "In progress · identity", title: "Finish verifying on your other device", body: "We've opened a secure verification session. Complete it on the device you started on — this page will update on its own the moment you're done." },
    in_review: { kicker: "Almost done · identity", title: "We're finalizing your verification", body: "Thanks — your details were submitted and are being reviewed. This usually takes only a moment. This page will update automatically." },
    blocked: { kicker: "Verification unavailable", title: "We couldn't verify your identity", body: st.blocked_reason || "You've reached the maximum number of verification attempts. Please contact your AriaOne administrator to continue." },
  };
  const c = COPY[state] || COPY.pending;
  const working = state === "in_review";

  const primaryBtn = (() => {
    if (state === "blocked" || state === "in_review") return null;
    if (hasActive || state === "in_progress") {
      return (
        <button onClick={openActive} disabled={!hasActive} data-testid="verify-continue-btn"
          style={btnPrimary(hasActive)}>Continue verification ↗</button>
      );
    }
    return (
      <button onClick={startSession} disabled={!canStart || busy} data-testid="verify-start-btn"
        style={btnPrimary(canStart && !busy)}>
        {busy ? (<><Spinner /> Opening secure check…</>) : state === "reverify_required" ? "Re-verify my identity ↗" : "Start verification ↗"}
      </button>
    );
  })();

  return (
    <div data-testid="verify-gate" style={{ position: "fixed", inset: 0, zIndex: 9000, background: "#F1F1EF", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ width: "min(520px, 96vw)", background: "#fff", border: "1px solid #E6E6E4", borderRadius: 18, boxShadow: "0 30px 80px rgba(20,20,18,.18)", overflow: "hidden" }}>
        {/* header */}
        <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "18px 24px", borderBottom: "1px solid #EEEEEC" }}>
          <Mark size={30} />
          <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 10.5, fontWeight: 800, letterSpacing: ".05em", color: state === "blocked" ? "#B3261E" : "#9A5A00", textTransform: "uppercase" }}>
              <span style={{ width: 6, height: 6, borderRadius: 999, background: state === "blocked" ? "#B3261E" : "#FF9500" }} />{c.kicker}
            </span>
            <span style={{ fontSize: 16, fontWeight: 800, color: INK, letterSpacing: "-.01em" }}>Identity verification</span>
          </div>
        </div>

        {/* body */}
        <div style={{ padding: "22px 24px 8px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {working ? <Spinner dark /> : null}
            <h2 data-testid="verify-title" style={{ fontSize: 19, fontWeight: 800, color: INK, margin: 0, letterSpacing: "-.01em" }}>{c.title}</h2>
          </div>
          <p data-testid="verify-body" style={{ fontSize: 13.5, lineHeight: 1.55, color: "#4A4A46", margin: "10px 0 0" }}>{c.body}</p>

          {state === "in_progress" || hasActive ? (
            <div style={{ marginTop: 14, fontSize: 12, lineHeight: 1.5, color: "#6C6C69", background: "#F7F7F6", borderRadius: 10, padding: "10px 12px" }}>
              Most people scan a QR code on the verification page with their phone and finish there. Keep this window open — it checks every few seconds and lets you in automatically.
            </div>
          ) : null}

          {state !== "blocked" && attemptsLeft !== null ? (
            <div data-testid="verify-attempts" style={{ marginTop: 14, fontSize: 11.5, color: "#9B9B97" }}>
              {attemptsLeft} of {st.max_attempts} attempt{st.max_attempts === 1 ? "" : "s"} remaining
              {cooldown > 0 ? <> · you can start again in {cooldown}s</> : null}
            </div>
          ) : null}

          {err ? <div data-testid="verify-error" style={{ marginTop: 12, fontSize: 12, color: "#B3261E", background: "#FDEDEC", border: "1px solid #F3D3D0", borderRadius: 9, padding: "9px 11px" }}>{err}</div> : null}
        </div>

        {/* footer actions */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 24px 20px" }}>
          <button onClick={signOut} data-testid="verify-signout" style={{ fontSize: 12.5, fontWeight: 700, color: "#6C6C69", background: "transparent", border: 0, cursor: "pointer" }}>Sign out</button>
          <span style={{ flex: 1 }} />
          {primaryBtn}
        </div>
      </div>
      <div style={{ marginTop: 14, fontSize: 11, color: "#9B9B97", display: "flex", alignItems: "center", gap: 6 }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9B9B97" strokeWidth="2"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
        Secure identity check powered by Didit. AriaOne never sees your ID document.
      </div>
    </div>
  );
}

function btnPrimary(enabled) {
  return {
    display: "inline-flex", alignItems: "center", gap: 8,
    background: enabled ? INK : "#E4E4E1", color: enabled ? "#fff" : "#9B9B97",
    border: 0, borderRadius: 999, padding: "12px 20px", fontWeight: 800, fontSize: 13.5,
    cursor: enabled ? "pointer" : "default",
  };
}
